import React, { useState, useMemo, useCallback } from 'react';
import {
  Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, Box, IconButton, Tooltip, Checkbox, Button, Chip,
  InputAdornment, Typography, Snackbar, Alert, Divider, Menu, MenuItem, ListItemIcon,
  ListItemText,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchIcon from '@mui/icons-material/Search';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { REVIEW_STATUS, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../../utils/constants';
import { updateUserReviewStatus, bulkUpdateReviewStatus } from '../../services/api';
import ActionDialog from './ActionDialog';

const FONT = '0.73rem';
const FONT_MONO = '0.7rem';
const HEADER_FONT = '0.65rem';
const FILTER_FONT = '0.62rem';

const STATUS_STYLES = {
  [REVIEW_STATUS.APPROVED]: { bgcolor: '#E8F5E9', color: '#1B8A2D', label: 'Retain' },
  [REVIEW_STATUS.REJECTED]: { bgcolor: '#FFEBEE', color: '#C62828', label: 'Revoke' },
  [REVIEW_STATUS.PENDING]: { bgcolor: '#FFF3E0', color: '#E65100', label: 'Pending' },
};

// ── Column definitions ───────────────────────────────────────────────────────
const COLUMNS = [
  { id: 'userId', label: 'User Id', mono: true, w: 95 },
  { id: 'fullUsername', label: 'Full Username', bold: true, sticky: 'left', w: 160 },
  { id: 'loginCount', label: 'Login Count', mono: true, sticky: 'left', w: 75 },
  { id: 'userEmail', label: 'User Email', w: 170 },
  { id: 'title', label: 'Title', w: 90 },
  { id: 'primaryBrand', label: 'Primary Brand', w: 100 },
  { id: 'multiBrandAssignment', label: 'Multi-Brand', w: 90 },
  { id: 'primaryJobRoleCode', label: 'Job Role Code', mono: true, w: 85 },
  { id: 'jobDescription', label: 'Job Description', w: 140 },
  { id: 'workCountry', label: 'Country', w: 70 },
  { id: 'businessUnitName', label: 'Business Unit', w: 120 },
  { id: 'department', label: 'Department', w: 110 },
  { id: 'businessMarketUnit', label: 'Market Unit', w: 100 },
  { id: 'manager', label: 'Manager', w: 130 },
  { id: 'managerEmail', label: 'Manager Email', w: 170 },
  { id: 'employmentStatus', label: 'Emp. Status', w: 80 },
  { id: 'lastAccess', label: 'Last Access', mono: true, w: 90 },
  { id: 'accessStatus', label: 'Access Status', w: 80 },
  { id: 'userAccessGroup', label: 'Access Group', w: 100 },
  { id: 'reviewedBy', label: 'Reviewed By', w: 120 },
];

const ALWAYS_VISIBLE = ['fullUsername', 'loginCount'];
const NUMERIC_COLS = new Set(['loginCount']);
const DATE_COLS    = new Set(['lastAccess']);

const EXPORT_COLUMNS = [
  { id: 'recordId', label: 'Record ID' },
  { id: 'appId', label: 'Application ID' },
  { id: 'userId', label: 'User ID' },
  { id: 'fullUsername', label: 'Full Username' },
  { id: 'loginCount', label: 'Login Count' },
  { id: 'userEmail', label: 'User Email' },
  { id: 'title', label: 'Title' },
  { id: 'primaryBrand', label: 'Primary Brand' },
  { id: 'multiBrandAssignment', label: 'Multi-Brand Assignment' },
  { id: 'primaryJobRoleCode', label: 'Primary Job Role Code' },
  { id: 'jobDescription', label: 'Job Description' },
  { id: 'workCountry', label: 'Work Country' },
  { id: 'businessUnitName', label: 'Business Unit' },
  { id: 'department', label: 'Department' },
  { id: 'businessMarketUnit', label: 'Business Market Unit' },
  { id: 'manager', label: 'Manager' },
  { id: 'managerEmail', label: 'Manager Email' },
  { id: 'employmentStatus', label: 'Employment Status' },
  { id: 'lastAccess', label: 'Last Access' },
  { id: 'accessStatus', label: 'Access Status' },
  { id: 'userAccessGroup', label: 'Access Group' },
  { id: 'reviewStatus', label: 'Review Status' },
  { id: 'reviewComment', label: 'Review Comment' },
  { id: 'reviewedBy', label: 'Reviewed By' },
  { id: 'reviewedAt', label: 'Reviewed At' },
];

function escapeCsvValue(value) {
  if (value === null || value === undefined) return '';
  const stringValue = String(value).replace(/"/g, '""');
  return /[",\n]/.test(stringValue) ? `"${stringValue}"` : stringValue;
}

function downloadCsvFile(filename, rows) {
  const blob = new Blob([`\uFEFF${rows.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function buildExportFileName(appName,appId) {
  const safeAppId = String(appName || appId).replace(/[^a-z0-9-_]+/gi, '_');
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  return `${safeAppId}_access_review_${timestamp}.csv`;
}

// ── Sticky helpers ───────────────────────────────────────────────────────────
const stickyLeft = (left, zExtra = 0, showShadow = false) => ({
  position: 'sticky',
  left,
  zIndex: 2 + zExtra,
  bgcolor: 'inherit',
  ...(showShadow && {
    '&::after': {
      content: '""', position: 'absolute', top: 0, right: -6, bottom: 0, width: 6,
      background: 'linear-gradient(to right, rgba(0,0,0,0.04), transparent)',
    },
  }),
});

const stickyRight = (right, zExtra = 0, showShadow = false) => ({
  position: 'sticky',
  right,
  zIndex: 2 + zExtra,
  bgcolor: 'inherit',
  ...(showShadow && {
    '&::before': {
      content: '""', position: 'absolute', top: 0, left: -6, bottom: 0, width: 6,
      background: 'linear-gradient(to left, rgba(0,0,0,0.04), transparent)',
    },
  }),
});

const CHECKBOX_W = 40;
const STATUS_W = 80;
const ACTIONS_W = 70;

// ── Tiny filter input for column headers ─────────────────────────────────────
const colFilterSx = {
  '& .MuiInputBase-root': { fontSize: FILTER_FONT, height: 22, px: 0.5, bgcolor: '#fff', borderRadius: 0.5 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#DDE3EA' },
  '& .MuiInputBase-input': { py: '2px', px: '4px' },
  '& .MuiInputBase-input::placeholder': { fontSize: FILTER_FONT, opacity: 0.6 },
  mt: 0.5, width: '100%',
};

export default function UserAccessGrid({ appId, appName, accessPeriod, users, isSignedOff, onDataChange, onUsersUpdate }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [selected, setSelected] = useState([]);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // ── Column visibility ──────────────────────────────────────────────────────
  const [hiddenCols, setHiddenCols] = useState(['userEmail','title','multiBrandAssignment','primaryJobRoleCode','businessUnitName','department','businessMarketUnit','manager','accessStatus','reviewedBy']);
  const [colMenuAnchor, setColMenuAnchor] = useState(null);

  // ── Per-column filters ─────────────────────────────────────────────────────
  const [colFilters, setColFilters] = useState({});

  // ── Sorting ────────────────────────────────────────────────────────────────
  const [sortCol, setSortCol] = useState('loginCount');
  const [sortDir, setSortDir] = useState('desc');

  const handleSortClick = useCallback((colId) => {
    setSortCol((prev) => {
      if (prev === colId) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return colId;
      }
      setSortDir('asc');
      return colId;
    });
    setPage(0);
  }, []);

  const updateColFilter = useCallback((colId, value) => {
    setColFilters((prev) => ({ ...prev, [colId]: value }));
    setPage(0);
  }, []);

  // ── Dialog state ───────────────────────────────────────────────────────────
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState('Retain');
  const [dialogIsBulk, setDialogIsBulk] = useState(false);
  const [dialogRecordId, setDialogRecordId] = useState(null);
  const [dialogUserName, setDialogUserName] = useState('');
  const [dialogLoading, setDialogLoading] = useState(false);

  const hasCheckbox = !isSignedOff;

  // ── Visible columns ───────────────────────────────────────────────────────
  const visibleColumns = useMemo(
    () => COLUMNS.filter((c) => ALWAYS_VISIBLE.includes(c.id) || !hiddenCols.includes(c.id)),
    [hiddenCols]
  );

  const toggleableColumns = useMemo(
    () => COLUMNS.filter((c) => !ALWAYS_VISIBLE.includes(c.id)),
    []
  );

  const toggleColumnVisibility = (colId) => {
    setHiddenCols((prev) =>
      prev.includes(colId) ? prev.filter((id) => id !== colId) : [...prev, colId]
    );
  };

  // ── Sticky left offsets ───────────────────────────────────────────────────
  const stickyLeftCols = visibleColumns.filter((c) => c.sticky === 'left');
  const stickyOffsets = useMemo(() => {
    const offsets = {};
    let cumulative = hasCheckbox ? CHECKBOX_W : 0;
    stickyLeftCols.forEach((col) => {
      offsets[col.id] = cumulative;
      cumulative += (col.w || 100);
    });
    return offsets;
  }, [stickyLeftCols, hasCheckbox]);

  const lastStickyId = stickyLeftCols[stickyLeftCols.length - 1]?.id;
  const scrollableCols = visibleColumns.filter((c) => c.sticky !== 'left');

  // ── Filter (global + per-column), sort, paginate ─────────────────────────
  const filtered = useMemo(() => {
    let list = users;

    // Global search
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((u) =>
        COLUMNS.some((c) => String(u[c.id] ?? '').toLowerCase().includes(q))
      );
    }

    // Per-column filters
    const activeFilters = Object.entries(colFilters).filter(([, v]) => v && v.trim());
    if (activeFilters.length > 0) {
      list = list.filter((u) =>
        activeFilters.every(([colId, val]) => {
          if (NUMERIC_COLS.has(colId)) {
            const numVal = Number(val);
            if (!isNaN(numVal)) return Number(u[colId]) === numVal;
          }
          return String(u[colId] ?? '').toLowerCase().includes(val.toLowerCase());
        })
      );
    }

    // Sort by selected column
    return [...list].sort((a, b) => {
      const aVal = a[sortCol];
      const bVal = b[sortCol];
      if (NUMERIC_COLS.has(sortCol)) {
        const diff = (Number(aVal) || 0) - (Number(bVal) || 0);
        return sortDir === 'asc' ? diff : -diff;
      }
      if (DATE_COLS.has(sortCol)) {
        const aTs = aVal ? new Date(aVal).getTime() : 0;
        const bTs = bVal ? new Date(bVal).getTime() : 0;
        return sortDir === 'asc' ? aTs - bTs : bTs - aTs;
      }
      const strA = String(aVal ?? '').toLowerCase();
      const strB = String(bVal ?? '').toLowerCase();
      const cmp = strA < strB ? -1 : strA > strB ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [users, search, colFilters, sortCol, sortDir]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const isAllSelected = paginated.length > 0 && paginated.every((u) => selected.includes(u.recordId));

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelected((prev) => prev.filter((id) => !paginated.some((u) => u.recordId === id)));
    } else {
      const ids = paginated.map((u) => u.recordId);
      setSelected((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  const toggleSelect = (recordId) => {
    setSelected((prev) =>
      prev.includes(recordId) ? prev.filter((id) => id !== recordId) : [...prev, recordId]
    );
  };

  const getUserId = (recordId) => users.find((u) => u.recordId === recordId)?.userId ?? null;
  const getUserName = (recordId) => users.find((u) => u.recordId === recordId)?.fullUsername ?? '';

  // ── Dialog handlers ───────────────────────────────────────────────────────
  const openSingleDialog = (recordId, status) => {
    setDialogRecordId(recordId);
    setDialogAction(status);
    setDialogIsBulk(false);
    setDialogUserName(getUserName(recordId));
    setDialogOpen(true);
  };

  const openBulkDialog = (status) => {
    if (selected.length === 0) return;
    setDialogRecordId(null);
    setDialogAction(status);
    setDialogIsBulk(true);
    setDialogUserName('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (!dialogLoading) { setDialogOpen(false); setDialogRecordId(null); }
  };

  // ── Patch user records locally after a successful API call ──────────────────
  const patchUsersLocally = (recordIds, status, comment) => {
    const reviewer = JSON.parse(sessionStorage.getItem('ar_user') || '{}').userId || '';
    const now = new Date().toISOString();
    const idSet = new Set(recordIds);
    const updated = users.map((u) =>
      idSet.has(u.recordId)
        ? { ...u, reviewStatus: status, reviewAction: status, reviewComment: comment, reviewedBy: reviewer, reviewedAt: now }
        : u
    );
    if (onUsersUpdate) onUsersUpdate(updated);
  };

  const handleDialogConfirm = async (comment) => {
    setDialogLoading(true);
    try {
      if (dialogIsBulk) {
        const records = selected.map((recordId) => ({ recordId, userId: getUserId(recordId) }));
        const count = records.length;
        await bulkUpdateReviewStatus(appId, records, dialogAction);
        patchUsersLocally(selected, dialogAction, comment);
        setSelected([]);
        setToast({ open: true, message: `${count} user(s) ${dialogAction.toLowerCase()}d successfully`, severity: 'success' });
      } else {
        const userId = getUserId(dialogRecordId);
        await updateUserReviewStatus(appId, dialogRecordId, userId, dialogAction, comment);
        patchUsersLocally([dialogRecordId], dialogAction, comment);
        setToast({ open: true, message: `User ${dialogAction.toLowerCase()}d successfully`, severity: 'success' });
      }
    } catch (err) {
      setToast({ open: true, message: err.message, severity: 'error' });
    } finally {
      setDialogLoading(false);
      setDialogOpen(false);
    }
  };

  // ── Render cell ───────────────────────────────────────────────────────────
  const renderCell = (col, row) => {
    const val = row[col.id];
    const empty = val === null || val === undefined || val === '';
    if (empty) return null;
    if (col.bold) return <Typography sx={{ fontWeight: 600, color: '#0A1929', fontSize: FONT }}>{val}</Typography>;
    if (col.mono) {
      // Format date columns at render time (raw ISO string stored for correct sorting)
      if (DATE_COLS.has(col.id)) {
        const display = val ? new Date(val).toLocaleString() : '';
        return <Typography sx={{ fontFamily: 'monospace', fontSize: FONT_MONO, color: '#5A6A7A' }}>{display}</Typography>;
      }
      return <Typography sx={{ fontFamily: 'monospace', fontSize: FONT_MONO, color: '#5A6A7A' }}>{val}</Typography>;
    }
    if (col.id === 'accessStatus') {
      const isInUse = val?.toLowerCase().replace(/\s/g, '') === 'inuse';
      return <Chip label={val} size="small" sx={{ bgcolor: isInUse ? '#E8F5E9' : '#FFEBEE', color: isInUse ? '#1B8A2D' : '#C62828', fontWeight: 600, fontSize: '0.63rem', borderRadius: 1, height: 20 }} />;
    }
    if (col.id === 'employmentStatus') {
      return <Chip label={val} size="small" sx={{ bgcolor: val === 'Active' ? '#EBF5FF' : '#FFF3E0', color: val === 'Active' ? '#0D47A1' : '#E65100', fontWeight: 600, fontSize: '0.63rem', borderRadius: 1, height: 20 }} />;
    }
    if (col.id === 'userAccessGroup') {
      const isDirect = String(val).toUpperCase().startsWith('DIRECT_');
      const displayLabel = isDirect ? 'DIRECT' : val;
      return (
        <Tooltip title={isDirect ? 'Direct user assignment' : val} arrow placement="top">
          <Chip label={displayLabel} size="small" sx={{ bgcolor: '#F0F3F7', color: '#1A2027', fontWeight: 500, fontSize: '0.63rem', borderRadius: 1, height: 20, maxWidth: 90, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }} />
        </Tooltip>
      );
    }
    if (col.id === 'reviewedBy') {
      return <Typography sx={{ fontWeight: 500, color: '#0A1929', fontSize: FONT }}>{val}</Typography>;
    }
    return <Typography sx={{ fontSize: FONT }}>{val}</Typography>;
  };

  // ── Header cell with label + sort + filter input ──────────────────────────
  const renderHeaderCell = (col) => {
    const isActive = sortCol === col.id;
    return (
      <Box>
        <Box
          onClick={() => handleSortClick(col.id)}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.3, cursor: 'pointer', userSelect: 'none', '&:hover .sort-icon': { opacity: 1 } }}
        >
          <Typography sx={{ fontSize: HEADER_FONT, fontWeight: 600, color: isActive ? '#1976D2' : '#5A6A7A', textTransform: 'none', lineHeight: 1.3 }}>
            {col.label}
          </Typography>
          <Box className="sort-icon" sx={{ display: 'flex', opacity: isActive ? 1 : 0.3, color: isActive ? '#1976D2' : '#9CA3AF' }}>
            {isActive
              ? (sortDir === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 10 }} /> : <ArrowDownwardIcon sx={{ fontSize: 10 }} />)
              : <UnfoldMoreIcon sx={{ fontSize: 10 }} />}
          </Box>
        </Box>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Filter..."
          value={colFilters[col.id] || ''}
          onChange={(e) => updateColFilter(col.id, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          sx={colFilterSx}
        />
      </Box>
    );
  };

  const totalCols = (hasCheckbox ? 1 : 0) + visibleColumns.length + 1 + (hasCheckbox ? 1 : 0);

  // Check if any column filter is active
  const activeFilterCount = Object.values(colFilters).filter((v) => v && v.trim()).length;

  const handleExportCsv = () => {
    if (users.length === 0) {
      setToast({ open: true, message: 'No data available to export', severity: 'warning' });
      return;
    }

    const headerRow = EXPORT_COLUMNS.map((column) => escapeCsvValue(column.label)).join(',');
    const dataRows = users.map((user) =>
      EXPORT_COLUMNS.map((column) => escapeCsvValue(user[column.id])).join(',')
    );

    downloadCsvFile(buildExportFileName(appName,appId), [headerRow, ...dataRows]);
    setToast({ open: true, message: `Exported ${users.length} row(s) to CSV`, severity: 'success' });
  };

  return (
    <Card>
      <CardContent sx={{ p: 0, px: 3, pt: 3 }}>
        {/* ── Toolbar ──────────────────────────────────────────────────────── */}
        <Box sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            {/* ── Left: title + count + bulk actions ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TableChartOutlinedIcon sx={{ color: '#5A6A7A', fontSize: 18 }} />
              <Typography variant="h6" sx={{ fontSize: '0.9rem' }}>User Access List</Typography>
              {accessPeriod && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#6A1B9A', fontSize: '0.68rem', fontWeight: 600 }}>
                    Access Period:
                  </Typography>
                  <Chip label={accessPeriod} size="small" sx={{ bgcolor: '#F3E5F5', color: '#6A1B9A', fontWeight: 600, fontSize: '0.65rem', height: 22, borderRadius: 1.5 }} />
                </Box>
              )}
              <Chip label={`${filtered.length} users`} size="small" sx={{ bgcolor: '#EBF5FF', color: '#0D47A1', fontWeight: 700, ml: 1, fontSize: '0.65rem', height: 22 }} />
              {hasCheckbox && selected.length > 0 && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  <Typography variant="body2" sx={{ color: '#5A6A7A', fontWeight: 500, fontSize: '0.7rem' }}>{selected.length} selected</Typography>
                  <Button size="small" variant="contained" startIcon={<DoneAllIcon />}
                    onClick={() => openBulkDialog(REVIEW_STATUS.APPROVED)}
                    sx={{ bgcolor: '#1B8A2D', '&:hover': { bgcolor: '#14662A' }, fontSize: '0.65rem', py: 0.5 }}>
                    Retain
                  </Button>
                  <Button size="small" variant="contained" startIcon={<RemoveDoneIcon />}
                    onClick={() => openBulkDialog(REVIEW_STATUS.REJECTED)}
                    sx={{ bgcolor: '#C62828', '&:hover': { bgcolor: '#8E0000' }, fontSize: '0.65rem', py: 0.5 }}>
                    Revoke
                  </Button>
                </>
              )}
            </Box>

            {/* ── Right: filter chips + column settings ── */}
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadOutlinedIcon />}
                onClick={handleExportCsv}
                disabled={users.length === 0}
                sx={{
                  fontSize: '0.68rem',
                  py: 0.5,
                  textTransform: 'none',
                  color: '#1976D2',
                  borderColor: '#90CAF9',
                  bgcolor: '#EBF5FF',
                  '&:hover': {
                    borderColor: '#1976D2',
                    bgcolor: '#DDEEFF',
                  },
                }}
              >
                Export CSV
              </Button>
              {activeFilterCount > 0 && (
                <Chip label={`${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} active`} size="small"
                  onDelete={() => setColFilters({})}
                  sx={{ bgcolor: '#EBF5FF', color: '#0D47A1', fontWeight: 600, fontSize: '0.62rem', height: 22 }} />
              )}
              {hiddenCols.length > 0 && (
                <Chip label={`${hiddenCols.length} column${hiddenCols.length > 1 ? 's' : ''} hidden`} size="small"
                  onDelete={() => setHiddenCols([])}
                  sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 600, fontSize: '0.62rem', height: 22 }} />
              )}
              <Tooltip title="Show / Hide Columns" arrow>
                <IconButton size="small" onClick={(e) => setColMenuAnchor(e.currentTarget)}
                  sx={{ color: '#5A6A7A', border: '1px solid #E3E8EF', borderRadius: 1, p: 0.5 }}>
                  <SettingsOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={colMenuAnchor}
                open={Boolean(colMenuAnchor)}
                onClose={() => setColMenuAnchor(null)}
                PaperProps={{ sx: { maxHeight: 400, minWidth: 240 } }}
              >
                <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #E3E8EF' }}>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#5A6A7A', letterSpacing: 0.5 }}>
                    Toggle Columns
                  </Typography>
                </Box>
                {toggleableColumns.map((col) => {
                  const isVisible = !hiddenCols.includes(col.id);
                  return (
                    <MenuItem key={col.id} dense onClick={() => toggleColumnVisibility(col.id)} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        {isVisible
                          ? <VisibilityIcon sx={{ fontSize: 16, color: '#1976D2' }} />
                          : <VisibilityOffIcon sx={{ fontSize: 16, color: '#C4CDD5' }} />}
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ fontSize: '0.72rem', color: isVisible ? '#0A1929' : '#9CA3AF' }}>
                        {col.label}
                      </ListItemText>
                    </MenuItem>
                  );
                })}
                {hiddenCols.length > 0 && (
                  <Box sx={{ borderTop: '1px solid #E3E8EF', px: 2, py: 1 }}>
                    <Button size="small" onClick={() => setHiddenCols([])} sx={{ fontSize: '0.68rem', textTransform: 'none' }}>
                      Show All Columns
                    </Button>
                  </Box>
                )}
              </Menu>
            </Box>
          </Box>
          <TextField
            placeholder="Search by user ID, name, email, department, manager, job description..."
            size="small" value={search} fullWidth
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#9CA3AF', fontSize: 18 }} /></InputAdornment> }}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#F8FAFC', fontSize: '0.75rem', '& fieldset': { borderColor: '#E3E8EF' } } }}
          />
        </Box>

        <Divider />

        {/* ── Table ────────────────────────────────────────────────────────── */}
        <TableContainer sx={{ maxHeight: 600, borderLeft: 1, borderRight: 1, borderColor: '#E3E8EF' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {hasCheckbox && (
                  <TableCell padding="checkbox" sx={{
                    ...stickyLeft(0, 3), pl: 0.5, bgcolor: '#515253ff !important',
                    minWidth: CHECKBOX_W, maxWidth: CHECKBOX_W, verticalAlign: 'top',
                  }}>
                    <Checkbox checked={isAllSelected} onChange={toggleSelectAll} size="small" sx={{ color: '#9CA3AF', p: 0.3 }} />
                  </TableCell>
                )}
                {/* ── Sticky left header cells ── */}
                {stickyLeftCols.map((col) => (
                  <TableCell key={col.id} sx={{
                    ...stickyLeft(stickyOffsets[col.id], 3, col.id === lastStickyId),
                    bgcolor: '#F4F6F9 !important', verticalAlign: 'top',
                    minWidth: col.w, maxWidth: col.w,
                  }}>
                    {renderHeaderCell(col)}
                  </TableCell>
                ))}
                {/* ── Scrollable header cells ── */}
                {scrollableCols.map((col) => (
                  <TableCell key={col.id} sx={{
                    bgcolor: '#F4F6F9 !important', verticalAlign: 'top',
                    minWidth: col.w, maxWidth: col.w,
                  }}>
                    {renderHeaderCell(col)}
                  </TableCell>
                ))}
                {/* ── Status header ── */}
                <TableCell sx={{
                  ...stickyRight(hasCheckbox ? ACTIONS_W : 0, 3, true),
                  bgcolor: '#F4F6F9 !important', minWidth: STATUS_W, verticalAlign: 'top',
                }}>
                  <Box
                    onClick={() => handleSortClick('reviewStatus')}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.3, cursor: 'pointer', userSelect: 'none', '&:hover .sort-icon': { opacity: 1 } }}
                  >
                    <Typography sx={{ fontSize: HEADER_FONT, fontWeight: 600, color: sortCol === 'reviewStatus' ? '#1976D2' : '#5A6A7A' }}>Status</Typography>
                    <Box className="sort-icon" sx={{ display: 'flex', opacity: sortCol === 'reviewStatus' ? 1 : 0.3, color: sortCol === 'reviewStatus' ? '#1976D2' : '#9CA3AF' }}>
                      {sortCol === 'reviewStatus'
                        ? (sortDir === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 10 }} /> : <ArrowDownwardIcon sx={{ fontSize: 10 }} />)
                        : <UnfoldMoreIcon sx={{ fontSize: 10 }} />}
                    </Box>
                  </Box>
                  <TextField size="small" variant="outlined" placeholder="Filter..."
                    value={colFilters['reviewStatus'] || ''}
                    onChange={(e) => updateColFilter('reviewStatus', e.target.value)}
                    onClick={(e) => e.stopPropagation()} sx={colFilterSx} />
                </TableCell>
                {/* ── Actions header ── */}
                {hasCheckbox && (
                  <TableCell align="center" sx={{
                    ...stickyRight(0, 3), bgcolor: '#F4F6F9 !important', minWidth: ACTIONS_W, verticalAlign: 'top',
                  }}>
                    <Typography sx={{ fontSize: HEADER_FONT, fontWeight: 600, color: '#5A6A7A' }}>Actions</Typography>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={totalCols} align="center" sx={{ py: 6 }}>
                    <Typography sx={{ fontSize: FONT }} color="text.secondary">No users match your search criteria</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((row) => {
                  const st = STATUS_STYLES[row.reviewStatus] || STATUS_STYLES[REVIEW_STATUS.PENDING];
                  const isSelected = selected.includes(row.recordId);
                  const rowBg = isSelected ? '#EBF5FF' : '#fff';
                  return (
                    <TableRow key={row.recordId} selected={isSelected} sx={{ bgcolor: rowBg }}>
                      {hasCheckbox && (
                        <TableCell padding="checkbox" sx={{
                          ...stickyLeft(0), pl: 0.5, bgcolor: rowBg, minWidth: CHECKBOX_W, maxWidth: CHECKBOX_W,
                        }}>
                          <Checkbox checked={isSelected} onChange={() => toggleSelect(row.recordId)} size="small" sx={{ color: '#9CA3AF', p: 0.3 }} />
                        </TableCell>
                      )}
                      {/* ── Sticky left cells ── */}
                      {stickyLeftCols.map((col) => (
                        <TableCell key={col.id} sx={{
                          ...stickyLeft(stickyOffsets[col.id], 0, col.id === lastStickyId),
                          bgcolor: rowBg, minWidth: col.w, maxWidth: col.w, whiteSpace: 'nowrap', py: 0.5,
                        }}>
                          {renderCell(col, row)}
                        </TableCell>
                      ))}
                      {/* ── Scrollable cells ── */}
                      {scrollableCols.map((col) => (
                        <TableCell key={col.id} sx={{ py: 0.5, minWidth: col.w, maxWidth: col.w, wordBreak: 'break-word' }}>
                          {renderCell(col, row)}
                        </TableCell>
                      ))}
                      {/* ── Status ── */}
                      <TableCell sx={{
                        ...stickyRight(hasCheckbox ? ACTIONS_W : 0, 0, true), bgcolor: rowBg, minWidth: STATUS_W, py: 0.5,
                      }}>
                        <Chip label={st.label} size="small" sx={{
                          bgcolor: st.bgcolor, color: st.color,
                          fontWeight: 700, fontSize: '0.58rem', borderRadius: 1, minWidth: 58, height: 18,
                        }} />
                      </TableCell>
                      {/* ── Actions ── */}
                      {hasCheckbox && (
                        <TableCell align="center" sx={{
                          ...stickyRight(0), bgcolor: rowBg, minWidth: ACTIONS_W, py: 0.5,
                        }}>
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.3 }}>
                            <Tooltip title="Retain" arrow>
                              <span>
                                <IconButton size="small"
                                  onClick={() => openSingleDialog(row.recordId, REVIEW_STATUS.APPROVED)}
                                  disabled={row.reviewStatus === REVIEW_STATUS.APPROVED}
                                  sx={{ color: row.reviewStatus === REVIEW_STATUS.APPROVED ? '#A5D6A7' : '#1B8A2D', p: 0.3, '&:hover': { bgcolor: '#E8F5E9' } }}>
                                  <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Revoke" arrow>
                              <span>
                                <IconButton size="small"
                                  onClick={() => openSingleDialog(row.recordId, REVIEW_STATUS.REJECTED)}
                                  disabled={row.reviewStatus === REVIEW_STATUS.REJECTED}
                                  sx={{ color: row.reviewStatus === REVIEW_STATUS.REJECTED ? '#EF9A9A' : '#C62828', p: 0.3, '&:hover': { bgcolor: '#FFEBEE' } }}>
                                  <HighlightOffIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Pagination ───────────────────────────────────────────────────── */}
        <TablePagination
          component="div" count={filtered.length} page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          sx={{ borderTop: '1px solid #E3E8EF', '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.7rem' } }}
        />
      </CardContent>

      {/* ── Action Confirmation Dialog ─────────────────────────────────────── */}
      <ActionDialog
        open={dialogOpen}
        action={dialogAction}
        isBulk={dialogIsBulk}
        selectedCount={selected.length}
        userName={dialogUserName}
        onConfirm={handleDialogConfirm}
        onCancel={closeDialog}
        loading={dialogLoading}
      />

      {/* ── Toast ──────────────────────────────────────────────────────────── */}
      <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast({ ...toast, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast({ ...toast, open: false })} sx={{ fontSize: '0.72rem' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Card>
  );
}
